import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorMessages";
import type { Invitation } from "../types/team";

export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await supabase.rpc("revoke_invitation", {
        p_invitation_id: invitationId,
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onMutate: async (invitationId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["invitations"] });

      // Snapshot the previous value
      const previousInvitations = queryClient.getQueryData<Invitation[]>([
        "invitations",
      ]);

      // Optimistically remove the invitation from the UI
      queryClient.setQueryData<Invitation[]>(["invitations"], (old) => {
        if (!old) return [];
        return old.filter((invitation) => invitation.id !== invitationId);
      });

      // Return context with previous data for rollback
      return { previousInvitations };
    },
    onSuccess: () => {
      toast.success("Invitation revoked");
    },
    onError: (error: Error, _invitationId, context) => {
      // Rollback to previous state on error
      if (context?.previousInvitations) {
        queryClient.setQueryData(["invitations"], context.previousInvitations);
      }
      const message = getErrorMessage(error);
      toast.error(message);
    },
    onSettled: () => {
      // Refetch to ensure sync with server state
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}
